/**
 * CombatSystem.js
 *
 * Phase 7 — Surface combat for the KRAKEN 3D gameplay.
 *
 * Owns:
 *   - Player vitals (HP, shield with regen)
 *   - Player weapon (laser pistol with magazine, recoil, reload, projectile travel)
 *   - Enemy entities (Sentry Drone, Predator Beast, Hostile Native) with simple
 *     state-machine AI (patrol → detect → chase → attack → flee at low HP)
 *   - Projectile pool (player ↔ enemy bolts with sphere-vs-sphere hits)
 *   - Lifecycle hooks bubbled up to React via callbacks
 *
 * Composed into GameScene — call combat.tick(dt) from GameScene._tick,
 * and ask combat.fire() / combat.reload() on input events. Enemy spawning
 * is tile-keyed to follow streamed terrain.
 *
 * Pure Babylon.js + plain JS — no React imports.
 */

import {
  Vector3,
  Quaternion,
  Color3,
  Color4,
  MeshBuilder,
  StandardMaterial,
  TransformNode,
} from '@babylonjs/core';

// --- Tunables ---

const PISTOL = {
  name: 'Laser Pistol',
  magSize: 12,
  fireInterval: 0.45,
  reloadTime: 1.8,
  projectileSpeed: 90,
  projectileLife: 2.4,    // seconds before despawn
  damage: 28,             // per hit
  recoilPitch: 0.045,     // radians kicked up per shot
  recoilSway: 0.018,      // radians of yaw drift per shot
};

// Ship-mounted twin laser cannons (used while piloting in space)
const SHIP_LASER = {
  name: 'Twin Pulse Laser',
  fireInterval: 0.22,    // both barrels alternate at this cadence
  projectileSpeed: 520,
  projectileLife: 3.2,
  damage: 22,            // per hit (single barrel)
  hitRadius: 4.0,        // enemy-ship collision radius
};

const ENEMY_SHIP = {
  name: 'Marauder',
  hp: 130,
  speed: 38,               // cruise toward / strafe around player
  visionRange: 1600,
  attackRange: 700,
  attackInterval: 1.2,
  bulletSpeed: 260,
  bulletDamage: 18,
  bountyCredits: 220,
  hitRadius: 3.6,          // size for player-laser collisions
  bodyColor: '#c84038',    // hostile red
  glowColor: '#ff7050',
};

const ARCHETYPES = {
  drone: {
    name: 'Sentry Drone',
    hp: 40,
    speed: 5,
    visionRange: 60,
    attackRange: 50,
    attackInterval: 1.4,
    bulletSpeed: 60,
    bulletDamage: 15,
    fleeHpFrac: 0.0,        // never flees
    bountyCredits: 35,
    color: '#9aa0a6',
    floatHeight: 4.2,
    biomes: ['Moon', 'Dwarf', 'Lava', 'Ice Giant'],
  },
  beast: {
    name: 'Predator Beast',
    hp: 55,
    speed: 6,
    chargeSpeed: 11,
    visionRange: 40,
    attackRange: 2.6,
    attackInterval: 1.1,
    bulletSpeed: 0,         // melee
    bulletDamage: 22,
    fleeHpFrac: 0.15,
    bountyCredits: 60,
    color: '#b06030',
    floatHeight: 0.0,
    biomes: ['Terrestrial', 'Ocean'],
  },
  native: {
    name: 'Hostile Native',
    hp: 35,
    speed: 4,
    visionRange: 80,
    attackRange: 60,
    attackInterval: 0.9,
    bulletSpeed: 70,
    bulletDamage: 12,
    fleeHpFrac: 0.30,
    bountyCredits: 50,
    color: '#6080aa',
    floatHeight: 0.0,
    biomes: ['Terrestrial'],   // habitability ≥ 40 only (gated below)
  },
};

const BIOME_HOSTILITY = {
  Lava: 0.7,
  Dwarf: 0.55,
  Terrestrial: 0.6,
  Ocean: 0.3,
  Moon: 0.4,
  'Ice Giant': 0.35,
  'Gas Giant': 0.15,
  Default: 0.4,
};

// Fraction of bodies that harbour hostile creatures at all. A per-body gate
// (deterministic from the body seed) keeps most planets/moons peaceful — only
// roughly this share are dangerous, instead of nearly every body.
const HOSTILE_BODY_CHANCE = 0.3;

const TILE_SIZE = 200;            // world meters per enemy tile
const MAX_ENEMIES = 8;            // hard cap concurrent
const PLAYER_HIT_RADIUS = 1.0;
const ENEMY_HIT_RADIUS = 1.6;
const SHIELD_REGEN_DELAY = 4.0;   // s after last damage
const SHIELD_REGEN_RATE = 40.0;   // SP per second (scaled for the larger shield pool)
const HP_REGEN_DELAY = 7.0;       // s after last damage (slower than shield)
const HP_REGEN_RATE = 3.0;        // HP per second
const VITALS_EMIT_INTERVAL = 0.25; // s between HUD pushes during regen

// Hash helpers (mirror GameScene._hash style; kept local for independence)
function hash2(x, y, seed) {
  let h = (x * 374761393 + y * 668265263 + seed) | 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177);
  h = (h ^ (h >>> 16)) >>> 0;
  return (h >>> 0) / 4294967295;
}

function hexToColor3(hex) {
  const h = (hex || '#888888').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  return new Color3(r, g, b);
}

export class CombatSystem {
  constructor(scene, opts) {
    this.scene = scene;
    this.opts = opts || {};
    this.getPlayerPos = opts.getPlayerPos;       // () => Vector3
    this.getPlayerForward = opts.getPlayerForward; // () => Vector3
    this.getCameraPos = opts.getCameraPos;       // () => Vector3 (for projectile origin in FPS)
    this.getTerrainHeight = opts.getTerrainHeight || ((_x, _z) => 0);
    this.getBodyType = opts.getBodyType || (() => 'Default');
    this.getBodyHabitability = opts.getBodyHabitability || (() => 0);
    this.getBodySeed = opts.getBodySeed || (() => 1337);
    this.isCombatActive = opts.isCombatActive || (() => true);
    // Ship-combat hooks (provided by GameScene when piloting in space mode)
    this.getShipPos = opts.getShipPos || null;        // () => Vector3
    this.getShipForward = opts.getShipForward || null;// () => Vector3
    this.getShipMuzzles = opts.getShipMuzzles || null;// () => [Vector3 leftWingTip, Vector3 rightWingTip] world space
    this.isShipCombatActive = opts.isShipCombatActive || (() => false);

    // Callbacks bubbled up to React
    this.onDamageTaken = opts.onDamageTaken || (() => {});
    this.onDeath = opts.onDeath || (() => {});
    this.onKill = opts.onKill || (() => {});
    this.onWeaponState = opts.onWeaponState || (() => {});
    this.onEnemiesChanged = opts.onEnemiesChanged || (() => {});
    this.onFire = opts.onFire || (() => {});
    this.onProjectileImpact = opts.onProjectileImpact || (() => {});
    // Fired periodically while regen is changing hp/shield so the HUD can
    // animate the bars filling back up. Throttled to avoid spamming React.
    this.onVitalsRegen = opts.onVitalsRegen || (() => {});

    // Player vitals (synced from React via setVitals)
    this.hp = 100;
    this.maxHp = 100;
    this.shield = 500;
    this.maxShield = 500;
    this.dead = false;
    this._lastDamagedAt = -999;
    this._vitalsEmitAccum = 0;

    // Weapon state
    this.currentMag = PISTOL.magSize;
    this.fireCooldown = 0;
    this.reloading = false;
    this.reloadProgress = 0; // 0..1
    this.recoilPitch = 0;
    this.recoilDecay = 6.0;

    // Active projectiles and enemies
    this._projectiles = [];         // { mesh, vel, life, damage, owner: 'player'|'enemy'|'player_ship'|'enemy_ship' }
    this._enemies = new Map();      // id -> enemy obj
    this._enemyShips = new Map();   // id -> enemy ship obj
    this._enemyParent = new TransformNode('enemiesRoot', scene);
    this._enemyShipParent = new TransformNode('enemyShipsRoot', scene);
    this._projParent = new TransformNode('projectilesRoot', scene);

    // Ship weapon
    this._shipFireCooldown = 0;
    this._shipFireToggle = 0; // alternate left/right muzzle
    this._shipCombatActive = false;
    this._shipCombatSeed = 0;

    // Tile bookkeeping for streamed enemy population
    this._tileCenters = new Set();
    this._lastTileCenter = null;

    this._emitWeapon();
  }

  // -------- Public API --------

  /**
   * Cave mode — suspends deterministic surface tile-streaming so the only
   * enemies present are the ones spawned explicitly via spawnCaveEnemy().
   */
  setCaveMode(enabled) {
    this._caveMode = !!enabled;
  }

  /** Spawn a single hostile creature at a cave-floor position. */
  spawnCaveEnemy(x, z, opts = {}) {
    const key = opts.archetypeKey || 'beast';
    const id = opts.id || `cave_${Math.round(x)}_${Math.round(z)}_${this._enemies.size}`;
    if (this._enemies.has(id)) return;
    const e = this._createEnemy({ id, archetypeKey: key, spawnX: x, spawnZ: z, isElite: false });
    if (e) {
      this._enemies.set(id, e);
      try { this.onEnemiesChanged(this.getEnemyPois()); } catch (_err) { /* noop */ }
    }
  }

  /** Dispose every active surface/cave enemy (used on cave enter/exit). */
  clearAllEnemies() {
    for (const [id, e] of Array.from(this._enemies.entries())) {
      this._destroyEnemy(e);
      this._enemies.delete(id);
    }
    this._lastTileCenter = null; // force a fresh surface re-stream afterwards
    try { this.onEnemiesChanged(this.getEnemyPois()); } catch (_err) { /* noop */ }
  }

  setVitals({ hp, maxHp, shield, maxShield }) {
    if (typeof hp === 'number') this.hp = hp;
    if (typeof maxHp === 'number') this.maxHp = maxHp;
    if (typeof shield === 'number') this.shield = shield;
    if (typeof maxShield === 'number') this.maxShield = maxShield;
    this.dead = this.hp <= 0;
  }

  /** Phase 1 — weapon module multipliers (damage + fire rate). */
  setWeaponMods({ damageMult, fireRateMult } = {}) {
    this._weaponDamageMult = Math.max(1, Number(damageMult) || 1);
    this._weaponFireRateMult = Math.max(1, Number(fireRateMult) || 1);
  }

  fire() {
    if (this.dead || !this.isCombatActive()) return false;
    if (this.reloading || this.fireCooldown > 0) return false;
    if (this.currentMag <= 0) {
      this.reload();
      return false;
    }
    this.currentMag -= 1;
    this.fireCooldown = PISTOL.fireInterval / (this._weaponFireRateMult || 1);
    this.recoilPitch = Math.max(this.recoilPitch, PISTOL.recoilPitch);

    // Spawn projectile from camera position toward camera forward
    const origin = (this.getCameraPos && this.getCameraPos()) || this.getPlayerPos();
    const fwd = this.getPlayerForward();
    if (!origin || !fwd) return false;
    const muzzle = origin.add(fwd.scale(0.6));
    this._spawnProjectile({
      pos: muzzle,
      dir: fwd.normalize(),
      speed: PISTOL.projectileSpeed,
      life: PISTOL.projectileLife,
      damage: PISTOL.damage * (this._weaponDamageMult || 1),
      owner: 'player',
      color: '#ff2a2a',
    });
    this._emitWeapon();
    try { this.onFire({ weapon: 'pistol', pos: muzzle, dir: fwd.normalize() }); } catch (_e) { /* noop */ }
    return true;
  }

  reload() {
    if (this.dead) return;
    if (this.reloading) return;
    if (this.currentMag >= PISTOL.magSize) return;
    this.reloading = true;
    this.reloadProgress = 0;
    this._emitWeapon();
  }

  /**
   * Fire the ship's twin pulse lasers from the wing-tip muzzles.
   * Used only while piloting in space mode. Bypasses pistol mag / reload.
   * Returns true if a shot was actually emitted.
   */
  fireShipLaser() {
    if (this.dead) return false;
    if (!this.isShipCombatActive()) return false;
    if (this._shipFireCooldown > 0) return false;
    if (!this.getShipMuzzles || !this.getShipForward) return false;
    const muzzles = this.getShipMuzzles();
    const fwd = this.getShipForward();
    if (!muzzles || muzzles.length < 2 || !fwd) return false;
    this._shipFireCooldown = SHIP_LASER.fireInterval / (this._weaponFireRateMult || 1);
    // Alternate left / right barrel each shot for a "pulse" rhythm
    const idx = this._shipFireToggle ? 1 : 0;
    this._shipFireToggle = !this._shipFireToggle;
    const muzzle = muzzles[idx];
    const dir = fwd.normalize();
    this._spawnProjectile({
      pos: muzzle.clone(),
      dir,
      speed: SHIP_LASER.projectileSpeed,
      life: SHIP_LASER.projectileLife,
      damage: SHIP_LASER.damage * (this._weaponDamageMult || 1),
      owner: 'player_ship',
      color: '#ff2a2a',
      size: 0.7,
    });
    try { this.onFire({ weapon: 'ship_laser', pos: muzzle.clone(), dir }); } catch (_e) { /* noop */ }
    return true;
  }

  /**
   * Toggle ship-combat lifecycle. When enabled, GameScene should also call
   * `seedEnemyShips(...)` to populate the encounter. When disabled the
   * encounter is despawned.
   */
  setShipCombatEnabled(enabled, opts = {}) {
    this._shipCombatActive = !!enabled;
    // Always clear any existing encounter first so toggling, system
    // re-entry, or re-launching never accumulates ships.
    this._clearEnemyShips();
    // Also wipe lingering ship-class projectiles so they don't carry into
    // a surface scene where they'd be invisible/orphaned.
    const survivors = [];
    for (const p of this._projectiles) {
      if (p.owner === 'player_ship' || p.owner === 'enemy_ship') {
        try { p.mesh.dispose(); } catch (_e) { /* noop */ }
      } else {
        survivors.push(p);
      }
    }
    this._projectiles = survivors;
    if (!enabled) return;
    this._shipCombatSeed = (opts.seed | 0) ^ 0xa17e51;
    const count = Math.max(0, Math.min(6, opts.count || 3));
    const center = opts.center || (this.getShipPos ? this.getShipPos() : null);
    if (!center) return;
    const radiusMin = opts.radiusMin || 600;
    const radiusMax = opts.radiusMax || 1100;
    for (let i = 0; i < count; i++) {
      const r0 = hash2(i, 1, this._shipCombatSeed);
      const r1 = hash2(i, 2, this._shipCombatSeed);
      const r2 = hash2(i, 3, this._shipCombatSeed);
      const angle = r0 * Math.PI * 2;
      const dist = radiusMin + r1 * (radiusMax - radiusMin);
      const vert = (r2 - 0.5) * 280;
      const px = center.x + Math.cos(angle) * dist;
      const pz = center.z + Math.sin(angle) * dist;
      const py = center.y + vert;
      this._createEnemyShip(`es_${i}_${this._shipCombatSeed & 0xffff}`, new Vector3(px, py, pz));
    }
  }

  /** Returns POIs for the radar (kind: 'enemy') for both ground + ship foes. */

  /** Called when the player respawns. Restores combat-side state to defaults. */
  respawn({ hp = 70, shield = 250 } = {}) {
    this.hp = hp;
    this.shield = shield;
    this.dead = false;
    this.currentMag = PISTOL.magSize;
    this.reloading = false;
    this.reloadProgress = 0;
    this.fireCooldown = 0;
    this._emitWeapon();
  }

  /**
   * Per-frame tick. Call from GameScene._tick(dt).
   * Skips enemy/projectile work if `isCombatActive()` returns false (e.g. in ship / space).
   */
  tick(dt) {
    // Recoil decay
    if (this.recoilPitch > 0) {
      this.recoilPitch = Math.max(0, this.recoilPitch - this.recoilDecay * dt);
    }

    // Fire cooldown
    if (this.fireCooldown > 0) this.fireCooldown = Math.max(0, this.fireCooldown - dt);

    // Reload progression
    if (this.reloading) {
      this.reloadProgress = Math.min(1, this.reloadProgress + dt / PISTOL.reloadTime);
      if (this.reloadProgress >= 1) {
        this.reloading = false;
        this.currentMag = PISTOL.magSize;
        this.reloadProgress = 0;
        this._emitWeapon();
      } else if (Math.random() < 0.2) {
        this._emitWeapon();
      }
    }

    // Shield + HP regen (only when alive). Shield kicks in first (faster),
    // HP follows after a longer delay. Regen runs in every mode — including
    // when piloting or sitting in the ship — so the player can heal up while
    // travelling between systems.
    const since = (performance.now() / 1000) - this._lastDamagedAt;
    let regened = false;
    if (!this.dead && since > SHIELD_REGEN_DELAY && this.shield < this.maxShield) {
      this.shield = Math.min(this.maxShield, this.shield + SHIELD_REGEN_RATE * dt);
      regened = true;
    }
    if (!this.dead && since > HP_REGEN_DELAY && this.hp < this.maxHp) {
      this.hp = Math.min(this.maxHp, this.hp + HP_REGEN_RATE * dt);
      regened = true;
    }
    if (regened) {
      // Throttle the HUD push so React doesn't re-render every frame.
      this._vitalsEmitAccum += dt;
      if (this._vitalsEmitAccum >= VITALS_EMIT_INTERVAL) {
        this._vitalsEmitAccum = 0;
        try { this.onVitalsRegen({ hp: this.hp, shield: this.shield }); } catch (_e) { /* noop */ }
      }
    } else {
      this._vitalsEmitAccum = 0;
    }

    // Skip the rest when combat is suspended (in ship, space mode, etc.)
    const active = !this.dead && this.isCombatActive();

    // Update existing projectiles regardless (so player can still see bullets in flight),
    // but only do collisions / spawning while active.
    this._tickProjectiles(dt, active);

    if (active) {
      // In cave mode enemies are spawned explicitly (no surface tile streaming).
      if (!this._caveMode) this._streamEnemies();
      this._tickEnemies(dt);
    }

    // --- Ship combat (piloting in space) ---
    if (this._shipFireCooldown > 0) {
      this._shipFireCooldown = Math.max(0, this._shipFireCooldown - dt);
    }
    if (!this.dead && this.isShipCombatActive()) {
      this._tickEnemyShips(dt);
    }
  }

  /** Return POI list for the mini-radar / compass. World coords (x, z). */
  getEnemyPois() {
    const out = [];
    for (const e of this._enemies.values()) {
      if (!e._alive) continue;
      out.push({
        id: `enemy_${e.id}`,
        kind: 'enemy',
        x: e.position.x,
        z: e.position.z,
        label: e.archetype.name,
      });
    }
    for (const s of this._enemyShips.values()) {
      if (!s._alive) continue;
      out.push({
        id: `enemy_ship_${s.id}`,
        kind: 'enemy',
        x: s.position.x,
        z: s.position.z,
        label: ENEMY_SHIP.name,
      });
    }
    return out;
  }

  getWeaponState() {
    return {
      weaponName: PISTOL.name,
      ammoCurrent: this.currentMag,
      ammoMax: PISTOL.magSize,
      reloading: this.reloading,
      reloadProgress: this.reloadProgress,
      recoilPitch: this.recoilPitch,
    };
  }

  dispose() {
    for (const p of this._projectiles) {
      try { p.mesh.dispose(); } catch (_e) { /* noop */ }
    }
    this._projectiles.length = 0;
    for (const e of this._enemies.values()) this._destroyEnemy(e);
    this._enemies.clear();
    this._clearEnemyShips();
    try { this._enemyParent.dispose(); } catch (_e) { /* noop */ }
    try { this._enemyShipParent.dispose(); } catch (_e) { /* noop */ }
    try { this._projParent.dispose(); } catch (_e) { /* noop */ }
  }

  // -------- Internals --------

  _emitWeapon() {
    try { this.onWeaponState(this.getWeaponState()); } catch (_e) { /* noop */ }
  }

  _applyDamageToPlayer(amount, source) {
    if (this.dead || !this.isCombatActive()) return;
    let dmg = amount;
    // Shield absorbs first
    if (this.shield > 0) {
      const absorbed = Math.min(this.shield, dmg);
      this.shield -= absorbed;
      dmg -= absorbed;
    }
    if (dmg > 0) this.hp = Math.max(0, this.hp - dmg);
    this._lastDamagedAt = performance.now() / 1000;
    try { this.onDamageTaken({ amount, hp: this.hp, shield: this.shield, source }); } catch (_e) { /* noop */ }
    if (this.hp <= 0) {
      this.dead = true;
      try { this.onDeath({ source }); } catch (_e) { /* noop */ }
    }
  }

  _spawnProjectile({ pos, dir, speed, life, damage, owner, color, size }) {
    const isShip = owner === 'player_ship' || owner === 'enemy_ship';
    const d = dir.normalize();
    // A thin, long CYLINDER reads as a true laser bolt — a scaled sphere still
    // looked like a little ball. Ship bolts are longer/fatter than hand shots.
    const radius = (typeof size === 'number' ? size : 0.35) * (isShip ? 0.5 : 0.32);
    const height = isShip ? 16 : 9;
    const mesh = MeshBuilder.CreateCylinder(`bolt_${owner}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, {
      height,
      diameterTop: radius * 2,
      diameterBottom: radius * 2,
      tessellation: 6,
    }, this.scene);
    const mat = new StandardMaterial(`boltMat_${owner}_${Math.random().toString(36).slice(2, 7)}`, this.scene);
    mat.disableLighting = true;
    // Hot neon core — drive it bright so the glow layer / bloom blow it out.
    mat.emissiveColor = hexToColor3(color).scale(3.6);
    mat.fogEnabled = false;
    mesh.material = mat;
    mesh.parent = this._projParent;
    mesh.position = pos.clone();
    mesh.isPickable = false;
    // Align the cylinder's local +Y (its length axis) with travel direction.
    const up = Vector3.Up();
    const dot = Math.max(-1, Math.min(1, Vector3.Dot(up, d)));
    if (dot > 0.99999) {
      mesh.rotationQuaternion = Quaternion.Identity();
    } else if (dot < -0.99999) {
      mesh.rotationQuaternion = Quaternion.RotationAxis(new Vector3(1, 0, 0), Math.PI);
    } else {
      const axis = Vector3.Cross(up, d).normalize();
      mesh.rotationQuaternion = Quaternion.RotationAxis(axis, Math.acos(dot));
    }
    this._projectiles.push({
      mesh,
      vel: dir.normalize().scale(speed),
      life,
      damage,
      owner,
    });
    // Hard cap projectile count
    if (this._projectiles.length > 120) {
      const stale = this._projectiles.shift();
      if (stale) try { stale.mesh.dispose(); } catch (_e) { /* noop */ }
    }
  }

  _tickProjectiles(dt, active) {
    if (this._projectiles.length === 0) return;
    const survivors = [];
    const playerPos = this.getPlayerPos();
    const shipPos = this.getShipPos ? this.getShipPos() : null;
    const shipCombatOn = this.isShipCombatActive ? this.isShipCombatActive() : false;
    for (const p of this._projectiles) {
      p.life -= dt;
      if (p.life <= 0) { try { p.mesh.dispose(); } catch (_e) { /* noop */ } continue; }
      p.mesh.position.addInPlace(p.vel.scale(dt));

      // Ground collision (only meaningful for surface projectiles)
      if (p.owner === 'player' || p.owner === 'enemy') {
        const gy = this.getTerrainHeight(p.mesh.position.x, p.mesh.position.z);
        if (p.mesh.position.y < gy) {
          try {
            this.onProjectileImpact({
              pos: p.mesh.position.clone(),
              owner: p.owner,
              kind: 'terrain',
            });
          } catch (_e) { /* noop */ }
          try { p.mesh.dispose(); } catch (_e) { /* noop */ }
          continue;
        }
      }

      let hit = false;
      let hitKind = 'unit';
      if (active && p.owner === 'player') {
        // Surface combat — check against ground enemies
        for (const e of this._enemies.values()) {
          if (!e._alive) continue;
          const d = Vector3.Distance(p.mesh.position, e.position);
          if (d < ENEMY_HIT_RADIUS + 0.5) {
            this._damageEnemy(e, p.damage, 'player');
            hit = true;
            break;
          }
        }
      } else if (active && p.owner === 'enemy' && playerPos) {
        const d = Vector3.Distance(p.mesh.position, playerPos);
        if (d < PLAYER_HIT_RADIUS + 0.4) {
          this._applyDamageToPlayer(p.damage, 'enemy_projectile');
          hit = true;
        }
      } else if (shipCombatOn && p.owner === 'player_ship') {
        // Ship combat — player ship bolts hit enemy ships
        for (const s of this._enemyShips.values()) {
          if (!s._alive) continue;
          const d = Vector3.Distance(p.mesh.position, s.position);
          if (d < ENEMY_SHIP.hitRadius + 0.4) {
            this._damageEnemyShip(s, p.damage, 'player_ship');
            hit = true;
            hitKind = 'unit';
            break;
          }
        }
      } else if (shipCombatOn && p.owner === 'enemy_ship' && shipPos) {
        // Enemy-ship bolts hit player ship — route through normal vitals
        const d = Vector3.Distance(p.mesh.position, shipPos);
        if (d < 4.0) {
          this._applyDamageToPlayer(p.damage, 'enemy_ship');
          hit = true;
        }
      }
      if (hit) {
        try {
          this.onProjectileImpact({
            pos: p.mesh.position.clone(),
            owner: p.owner,
            kind: hitKind,
          });
        } catch (_e) { /* noop */ }
        try { p.mesh.dispose(); } catch (_e) { /* noop */ }
        continue;
      }
      survivors.push(p);
    }
    this._projectiles = survivors;
  }

  _streamEnemies() {
    // Recompute the 3x3 tile grid around the player every time the center tile
    // changes. Each tile's enemy population is deterministic from (tileX, tileZ, bodySeed).
    const p = this.getPlayerPos();
    if (!p) return;
    const cx = Math.floor(p.x / TILE_SIZE);
    const cz = Math.floor(p.z / TILE_SIZE);
    const key = `${cx},${cz}`;
    if (this._lastTileCenter === key) return;
    this._lastTileCenter = key;

    // Build a fresh "wanted" set for the 5x5 surrounding tiles
    const wanted = new Map();
    for (let dx = -2; dx <= 2; dx++) {
      for (let dz = -2; dz <= 2; dz++) {
        const tx = cx + dx;
        const tz = cz + dz;
        const tileKey = `${tx}:${tz}`;
        for (const e of this._spawnTileEnemies(tx, tz)) {
          wanted.set(e.id, { ...e, tileKey });
        }
      }
    }

    // Destroy enemies whose tiles are no longer in scope
    for (const [id, ex] of Array.from(this._enemies.entries())) {
      if (!wanted.has(id)) {
        this._destroyEnemy(ex);
        this._enemies.delete(id);
      }
    }

    // Spawn new ones, respecting the global cap
    for (const [id, def] of wanted.entries()) {
      if (this._enemies.has(id)) continue;
      if (this._enemies.size >= MAX_ENEMIES) break;
      const e = this._createEnemy(def);
      if (e) this._enemies.set(id, e);
    }

    try { this.onEnemiesChanged(this.getEnemyPois()); } catch (_e) { /* noop */ }
  }

  /** Pure: returns the deterministic enemy spawn list for a given tile. */
  _spawnTileEnemies(tx, tz) {
    const seed = this.getBodySeed() | 0;
    // Per-body gate: only a fraction of bodies harbour hostile creatures.
    // Cached per seed so it's stable across every tile on the same body.
    if (this._hostileBodySeed !== seed) {
      this._hostileBodySeed = seed;
      this._bodyHasHostiles = hash2(0x484f, 0x5354, (seed ^ 0x6b4f5354) | 0) < HOSTILE_BODY_CHANCE;
    }
    if (!this._bodyHasHostiles) return [];

    const r = hash2(tx, tz, seed);
    const bodyType = this.getBodyType();
    const hostility = BIOME_HOSTILITY[bodyType] ?? BIOME_HOSTILITY.Default;
    const targetCount = r < hostility ? (1 + Math.floor(hash2(tx, tz, seed ^ 0x9e3779b9) * 2)) : 0;
    if (targetCount === 0) return [];

    // Cull tiles inside the safe radius around the spawn (player + ship origin = 0,0)
    const tileCenterX = tx * TILE_SIZE + TILE_SIZE / 2;
    const tileCenterZ = tz * TILE_SIZE + TILE_SIZE / 2;
    const distFromSpawn = Math.hypot(tileCenterX, tileCenterZ);
    if (distFromSpawn < 60) return [];

    const out = [];
    const habit = this.getBodyHabitability();
    // Phase 12d — at most ONE elite per tile (and only on hostile/named-pirate tiles)
    const eliteRoll = hash2(tx ^ 0xb01d, tz ^ 0xb01d, seed);
    let eliteSpawned = false;
    for (let i = 0; i < targetCount; i++) {
      const seed2 = seed ^ (i * 73856093);
      // Position within tile
      const px = tileCenterX + (hash2(tx, tz, seed2) - 0.5) * (TILE_SIZE - 30);
      const pz = tileCenterZ + (hash2(tz, tx, seed2 ^ 0x1234) - 0.5) * (TILE_SIZE - 30);

      // Choose archetype eligible for this biome (+habit rule for natives)
      const eligibleKeys = Object.entries(ARCHETYPES).filter(([k, a]) => {
        if (!a.biomes.includes(bodyType)) return false;
        if (k === 'native' && habit < 40) return false;
        return true;
      }).map(([k]) => k);
      if (eligibleKeys.length === 0) continue;
      const akey = eligibleKeys[Math.floor(hash2(tx, tz, seed2 + 11) * eligibleKeys.length) % eligibleKeys.length];

      // First eligible drone-or-pirate enemy in a roughly 1-in-3 tile is the elite captain.
      const isElite = !eliteSpawned && eliteRoll < 0.33 && akey !== 'native';
      if (isElite) eliteSpawned = true;

      out.push({
        id: `e_${tx}_${tz}_${i}`,
        archetypeKey: akey,
        spawnX: px,
        spawnZ: pz,
        isElite,
      });
    }
    return out;
  }

  _createEnemy(def) {
    const a = ARCHETYPES[def.archetypeKey];
    if (!a) return null;
    // Build a simple primitive mesh
    let mesh;
    if (def.archetypeKey === 'drone') {
      mesh = MeshBuilder.CreateSphere(`enemy_${def.id}`, { diameter: 1.4, segments: 14 }, this.scene);
    } else if (def.archetypeKey === 'beast') {
      mesh = MeshBuilder.CreateBox(`enemy_${def.id}`, { width: 1.6, height: 1.4, depth: 2.6 }, this.scene);
    } else {
      mesh = MeshBuilder.CreateCapsule(`enemy_${def.id}`, { radius: 0.5, height: 1.9, capSubdivisions: 6, tessellation: 12 }, this.scene);
    }
    const mat = new StandardMaterial(`enemyMat_${def.id}`, this.scene);
    if (def.isElite) {
      // Phase 12d — elite captain: deep purple, twice the size, glowing aura.
      mat.diffuseColor = new Color3(0.55, 0.25, 0.85);
      mat.emissiveColor = new Color3(0.55, 0.25, 0.85);
      mesh.scaling = new Vector3(1.6, 1.6, 1.6);
    } else {
      mat.diffuseColor = hexToColor3(a.color);
      mat.emissiveColor = hexToColor3(a.color).scale(0.18);
    }
    mesh.material = mat;
    mesh.parent = this._enemyParent;
    mesh.isPickable = false;

    const gy = this.getTerrainHeight(def.spawnX, def.spawnZ);
    const pos = new Vector3(def.spawnX, gy + a.floatHeight + 0.7, def.spawnZ);
    mesh.position = pos.clone();

    return {
      id: def.id,
      archetypeKey: def.archetypeKey,
      archetype: a,
      mesh,
      position: pos,
      velocity: new Vector3(0, 0, 0),
      hp: def.isElite ? a.hp * 2.5 : a.hp,
      maxHp: def.isElite ? a.hp * 2.5 : a.hp,
      isElite: !!def.isElite,
      state: 'patrol',  // patrol | chase | attack | flee
      patrolTarget: this._pickPatrolPoint(pos),
      lastShotAt: -999,
      _alive: true,
      _hitFlashAt: -999,
    };
  }

  _pickPatrolPoint(origin) {
    const angle = Math.random() * Math.PI * 2;
    const r = 18 + Math.random() * 28;
    return new Vector3(origin.x + Math.cos(angle) * r, 0, origin.z + Math.sin(angle) * r);
  }

  _damageEnemy(e, amount, source) {
    e.hp -= amount;
    e._hitFlashAt = performance.now() / 1000;
    if (e.hp <= 0) {
      e._alive = false;
      const a = e.archetype;
      // Phase 13 — anti-cheat: report which deterministic tile this kill was in
      const tileX = Math.floor(e.position.x / TILE_SIZE);
      const tileZ = Math.floor(e.position.z / TILE_SIZE);
      try {
        this.onKill({
          archetype: e.archetypeKey,
          name: e.isElite ? `${a.name} Captain` : a.name,
          credits: e.isElite ? Math.round(a.bountyCredits * 4.5) : a.bountyCredits,
          pos: { x: e.position.x, y: e.position.y, z: e.position.z },
          source,
          elite: !!e.isElite,
          tile_x: tileX,
          tile_z: tileZ,
        });
      } catch (_e) { /* noop */ }
      this._destroyEnemy(e);
      this._enemies.delete(e.id);
      try { this.onEnemiesChanged(this.getEnemyPois()); } catch (_e) { /* noop */ }
    }
  }

  _destroyEnemy(e) {
    try { e.mesh.dispose(); } catch (_e) { /* noop */ }
  }

  _tickEnemies(dt) {
    const player = this.getPlayerPos();
    if (!player) return;

    for (const e of this._enemies.values()) {
      if (!e._alive) continue;
      const a = e.archetype;

      // Hit-flash material restore
      const t = performance.now() / 1000;
      if (e._hitFlashAt > 0 && t - e._hitFlashAt < 0.12) {
        e.mesh.material.emissiveColor = new Color3(1, 0.6, 0.3);
      } else {
        e.mesh.material.emissiveColor = hexToColor3(a.color).scale(0.18);
      }

      const distToPlayer = Vector3.Distance(e.position, player);

      // State transitions
      if (e.state === 'patrol' && distToPlayer < a.visionRange) {
        e.state = 'chase';
      } else if ((e.state === 'chase' || e.state === 'attack')
                 && distToPlayer > a.visionRange * 1.5) {
        e.state = 'patrol';
        e.patrolTarget = this._pickPatrolPoint(e.position);
      }
      if (e.state === 'chase' && distToPlayer <= a.attackRange) {
        e.state = 'attack';
      } else if (e.state === 'attack' && distToPlayer > a.attackRange * 1.25) {
        e.state = 'chase';
      }
      // Flee if very wounded
      if (a.fleeHpFrac > 0 && e.hp <= e.maxHp * a.fleeHpFrac) {
        e.state = 'flee';
      }

      // Movement target by state
      let target = null;
      let speed = a.speed;
      if (e.state === 'patrol') {
        target = e.patrolTarget;
        if (Vector3.Distance(e.position, target) < 3) {
          e.patrolTarget = this._pickPatrolPoint(e.position);
        }
      } else if (e.state === 'chase') {
        target = player;
        if (e.archetypeKey === 'beast') speed = a.chargeSpeed || speed;
      } else if (e.state === 'flee') {
        const away = e.position.subtract(player);
        away.y = 0;
        if (away.lengthSquared() < 0.001) away.x = 1;
        const aw = away.normalize().scale(25);
        target = new Vector3(e.position.x + aw.x, 0, e.position.z + aw.z);
        speed = a.speed * 1.3;
      } else if (e.state === 'attack') {
        // Strafe slightly + face player (handled below)
        target = e.position;  // no chasing while attacking
      }

      if (target) {
        const dx = target.x - e.position.x;
        const dz = target.z - e.position.z;
        const d = Math.hypot(dx, dz);
        if (d > 0.15) {
          const step = Math.min(speed * dt, d);
          e.position.x += (dx / d) * step;
          e.position.z += (dz / d) * step;
        }
      }

      // Place enemy on terrain (drones float)
      const gy = this.getTerrainHeight(e.position.x, e.position.z);
      e.position.y = gy + a.floatHeight + 0.7;
      e.mesh.position.copyFrom(e.position);

      // Face the player while in any aggressive state
      if (e.state === 'chase' || e.state === 'attack') {
        const yaw = Math.atan2(player.x - e.position.x, player.z - e.position.z);
        e.mesh.rotation.y = yaw;
      }

      // Attack
      if (e.state === 'attack' && t - e.lastShotAt > a.attackInterval) {
        e.lastShotAt = t;
        if (a.bulletSpeed > 0) {
          // Ranged projectile aimed at the player (slight inaccuracy)
          const dir = player.subtract(e.position).normalize();
          dir.x += (Math.random() - 0.5) * 0.03;
          dir.y += (Math.random() - 0.5) * 0.02;
          dir.z += (Math.random() - 0.5) * 0.03;
          this._spawnProjectile({
            pos: e.position.add(new Vector3(0, 0.9, 0)),
            dir: dir.normalize(),
            speed: a.bulletSpeed,
            life: 2.0,
            damage: a.bulletDamage,
            owner: 'enemy',
            color: a.color,
          });
        } else {
          // Melee
          this._applyDamageToPlayer(a.bulletDamage, 'enemy_melee');
        }
      }
    }
  }

  // ---------- Ship combat (space) ----------

  _createEnemyShip(id, position) {
    const root = new TransformNode(`enemyShipRoot_${id}`, this.scene);
    root.parent = this._enemyShipParent;
    root.position = position.clone();

    const bodyMat = new StandardMaterial(`enemyShipMat_${id}`, this.scene);
    bodyMat.diffuseColor = hexToColor3(ENEMY_SHIP.bodyColor);
    bodyMat.emissiveColor = hexToColor3(ENEMY_SHIP.bodyColor).scale(0.35);
    bodyMat.specularColor = new Color3(0.4, 0.4, 0.4);

    const glowMat = new StandardMaterial(`enemyShipGlow_${id}`, this.scene);
    glowMat.disableLighting = true;
    glowMat.emissiveColor = hexToColor3(ENEMY_SHIP.glowColor).scale(1.6);
    glowMat.specularColor = new Color3(0, 0, 0);
    glowMat.fogEnabled = false;

    const hull = MeshBuilder.CreateSphere(`enemyHull_${id}`, {
      diameterX: 3.4, diameterY: 1.8, diameterZ: 5.2, segments: 14,
    }, this.scene);
    hull.material = bodyMat;
    hull.parent = root;
    hull.isPickable = false;

    const wing = MeshBuilder.CreateBox(`enemyWing_${id}`, {
      width: 6.4, height: 0.22, depth: 1.4,
    }, this.scene);
    wing.material = bodyMat;
    wing.position.y = 0.05;
    wing.parent = root;
    wing.isPickable = false;

    const eye = MeshBuilder.CreateSphere(`enemyEye_${id}`, { diameter: 0.6, segments: 10 }, this.scene);
    eye.position = new Vector3(0, 0.4, 2.2);
    eye.material = glowMat;
    eye.parent = root;
    eye.isPickable = false;

    // Engine glow at the rear
    const engine = MeshBuilder.CreateSphere(`enemyEngine_${id}`, { diameter: 0.9, segments: 10 }, this.scene);
    engine.position = new Vector3(0, 0, -2.7);
    engine.material = glowMat;
    engine.parent = root;
    engine.isPickable = false;

    const obj = {
      id,
      root,
      meshes: [hull, wing, eye, engine],
      bodyMat,
      glowMat,
      position: position.clone(),
      velocity: new Vector3(0, 0, 0),
      yaw: Math.random() * Math.PI * 2,
      hp: ENEMY_SHIP.hp,
      maxHp: ENEMY_SHIP.hp,
      lastShotAt: -999,
      _hitFlashAt: -999,
      _alive: true,
    };
    this._enemyShips.set(id, obj);
    try { this.onEnemiesChanged(this.getEnemyPois()); } catch (_e) { /* noop */ }
    return obj;
  }

  _destroyEnemyShip(s) {
    if (!s) return;
    s._alive = false;
    try {
      for (const m of s.meshes) m.dispose();
      s.root.dispose();
    } catch (_e) { /* noop */ }
  }

  _clearEnemyShips() {
    for (const s of this._enemyShips.values()) this._destroyEnemyShip(s);
    this._enemyShips.clear();
    try { this.onEnemiesChanged(this.getEnemyPois()); } catch (_e) { /* noop */ }
  }

  _damageEnemyShip(s, amount, source) {
    s.hp -= amount;
    s._hitFlashAt = performance.now() / 1000;
    if (s.hp <= 0) {
      try {
        this.onKill({
          archetype: 'enemy_ship',
          name: ENEMY_SHIP.name,
          credits: ENEMY_SHIP.bountyCredits,
          pos: { x: s.position.x, y: s.position.y, z: s.position.z },
          source,
          elite: false,
        });
      } catch (_e) { /* noop */ }
      // Big impact flash at death position
      try {
        this.onProjectileImpact({
          pos: s.position.clone(),
          owner: 'player_ship',
          kind: 'ship_explode',
        });
      } catch (_e) { /* noop */ }
      this._destroyEnemyShip(s);
      this._enemyShips.delete(s.id);
      try { this.onEnemiesChanged(this.getEnemyPois()); } catch (_e) { /* noop */ }
    }
  }

  _tickEnemyShips(dt) {
    if (this._enemyShips.size === 0) return;
    const shipPos = this.getShipPos ? this.getShipPos() : null;
    if (!shipPos) return;
    const t = performance.now() / 1000;
    for (const s of this._enemyShips.values()) {
      if (!s._alive) continue;
      // Hit flash restore
      if (s._hitFlashAt > 0 && t - s._hitFlashAt < 0.12) {
        s.bodyMat.emissiveColor = new Color3(1, 0.6, 0.3);
      } else {
        s.bodyMat.emissiveColor = hexToColor3(ENEMY_SHIP.bodyColor).scale(0.35);
      }
      const toPlayer = shipPos.subtract(s.position);
      const dist = toPlayer.length();
      if (dist > 0.001) toPlayer.scaleInPlace(1 / dist);
      // Maintain a stand-off distance — approach when far, drift when close
      const standoff = ENEMY_SHIP.attackRange * 0.6;
      let moveDir = toPlayer;
      let speed = ENEMY_SHIP.speed;
      if (dist < standoff) {
        // Strafe perpendicular to the chase vector to feel less static
        const perp = new Vector3(-toPlayer.z, 0, toPlayer.x);
        moveDir = perp;
        speed = ENEMY_SHIP.speed * 0.6;
      } else if (dist > ENEMY_SHIP.visionRange) {
        speed = ENEMY_SHIP.speed * 0.35;
      }
      // Smooth velocity toward target
      const desiredVel = moveDir.scale(speed);
      s.velocity.x += (desiredVel.x - s.velocity.x) * Math.min(1, dt * 1.2);
      s.velocity.y += (desiredVel.y - s.velocity.y) * Math.min(1, dt * 1.2);
      s.velocity.z += (desiredVel.z - s.velocity.z) * Math.min(1, dt * 1.2);
      s.position.addInPlace(s.velocity.scale(dt));
      s.root.position.copyFrom(s.position);
      // Face the player
      const yaw = Math.atan2(toPlayer.x, toPlayer.z);
      const pitch = -Math.atan2(toPlayer.y, Math.hypot(toPlayer.x, toPlayer.z));
      s.root.rotation = new Vector3(pitch, yaw, 0);

      // Fire when in range
      if (dist < ENEMY_SHIP.attackRange && t - s.lastShotAt > ENEMY_SHIP.attackInterval) {
        s.lastShotAt = t;
        const aim = toPlayer.clone();
        aim.x += (Math.random() - 0.5) * 0.04;
        aim.y += (Math.random() - 0.5) * 0.04;
        aim.z += (Math.random() - 0.5) * 0.04;
        const muzzle = s.position.clone();
        // Spawn slightly ahead of the eye
        muzzle.x += toPlayer.x * 2.4;
        muzzle.y += toPlayer.y * 2.4 + 0.3;
        muzzle.z += toPlayer.z * 2.4;
        this._spawnProjectile({
          pos: muzzle,
          dir: aim.normalize(),
          speed: ENEMY_SHIP.bulletSpeed,
          life: 3.8,
          damage: ENEMY_SHIP.bulletDamage,
          owner: 'enemy_ship',
          color: ENEMY_SHIP.glowColor,
          size: 0.55,
        });
      }
    }
  }
}

export default CombatSystem;
